import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import LanguageContext from "../helper/LanguageContext";
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import { $user } from "../helper/UserFactory";
import { SET_USER } from "../store";
import MaterialIcon from "./MaterialIcon";
import "../styles/kjuupIoSetup.css";

/**
 * Persist Kjuup.io workspace preferences (any subset) and mirror them into
 * Redux `user` + localStorage so the UI reacts immediately.
 */
export async function saveWorkspacePreferences(dispatch, currentUser, patch) {
  const { data } = await callAPI("/user/workspacePreferences", {}, "PUT", patch);
  if (!data || data.status === false) {
    throw new Error((data && data.message) || "workspacePreferences failed");
  }
  const next = {
    ...(currentUser || {}),
    chatsEnabled: data.chatsEnabled !== false,
    emailMode: data.emailMode === "reports" ? "reports" : "client",
    teamsMode: data.teamsMode === "reports" ? "reports" : "client",
    kjuupIoSetupDone: !!data.kjuupIoSetupDone,
  };
  dispatch({ type: SET_USER, user: next });
  $user.updateUserData(next);
  return next;
}

function OptionCard({ icon, title, text, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`io-setup-option${selected ? " io-setup-option--selected" : ""}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="io-setup-option-icon">
        <MaterialIcon icon={icon} size={22} color="currentColor" weight={400} />
      </span>
      <span className="io-setup-option-text">
        <span className="io-setup-option-title">{title}</span>
        <span className="io-setup-option-desc">{text}</span>
      </span>
      <span className="io-setup-option-check">
        <MaterialIcon icon={selected ? "check_circle" : "radio_button_unchecked"} size={22} color="currentColor" weight={selected ? 500 : 300} />
      </span>
    </button>
  );
}

/**
 * Two-step setup shown after the first Kjuup.io trial / booking (and from
 * the cockpit card until finished):
 *   1. Chats anzeigen?           -> chatsEnabled
 *   2. "E-Mail-Client + Projekte + Berichte" oder "Nur Projekte + Berichte" -> emailMode
 */
export default function KjuupIoSetupModal({ open, onClose, onSaved }) {
  const { strings } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user);
  const [step, setStep] = useState(1);
  const [chatsEnabled, setChatsEnabled] = useState(user?.chatsEnabled !== false);
  const [emailMode, setEmailMode] = useState(user?.emailMode === "reports" ? "reports" : "client");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setChatsEnabled(user?.chatsEnabled !== false);
    setEmailMode(user?.emailMode === "reports" ? "reports" : "client");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const finish = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const next = await saveWorkspacePreferences(dispatch, user, {
        chatsEnabled,
        emailMode,
        kjuupIoSetupDone: true,
      });
      $crud.notify({ type: "success", message: strings.kjuup_io_setup_saved || "Einrichtung gespeichert" });
      if (onSaved) onSaved(next);
      if (onClose) onClose();
    } catch (e) {
      $crud.notify({ type: "error", message: strings.something_went_wrong || "Etwas ist schiefgelaufen" });
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveWorkspacePreferences(dispatch, user, { kjuupIoSetupDone: true });
    } catch (_) {
      /* keep defaults */
    } finally {
      setSaving(false);
      if (onClose) onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="io-setup-overlay" role="dialog" aria-modal="true">
      <div className="io-setup-backdrop" onClick={onClose} />
      <div className="io-setup-dialog">
        <div className="io-setup-head">
          <div>
            <div className="io-setup-eyebrow">Kjuup.io · {strings.kjuup_io_setup_step || "Schritt"} {step}/2</div>
            <h2 className="io-setup-title">
              {step === 1
                ? strings.kjuup_io_setup_chats_title || "Möchtest du Chats nutzen?"
                : strings.kjuup_io_setup_email_title || "Wo möchtest du deine E-Mails lesen?"}
            </h2>
            <p className="io-setup-sub">
              {step === 1
                ? strings.kjuup_io_setup_chats_sub || "Chats mit Kolleg:innen und Projektpartnern sind optional. Du kannst sie jederzeit in den Einstellungen ein- oder ausblenden."
                : strings.kjuup_io_setup_email_sub || "Hier legst du nur fest, ob du einen E-Mail-Client in Kjuup nutzt oder weiter mit deinem bisherigen Programm arbeitest, zum Beispiel Outlook oder einem anderen. Du kannst später wechseln."}
            </p>
          </div>
          <button type="button" className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral io-setup-close" onClick={onClose} aria-label={strings.close || "Schließen"}>
            <MaterialIcon icon="close" size={20} color="currentColor" />
          </button>
        </div>

        <div className="io-setup-body">
          {step === 1 ? (
            <>
              <OptionCard
                icon="forum"
                title={strings.kjuup_io_setup_chats_yes || "Ja, Chats anzeigen"}
                text={strings.kjuup_io_setup_chats_yes_text || "Nachrichten enthält Chats und E-Mails. Projekte haben Gruppenchats für alle Beteiligten."}
                selected={chatsEnabled}
                onSelect={() => setChatsEnabled(true)}
              />
              <OptionCard
                icon="mail"
                title={strings.kjuup_io_setup_chats_no || "Nein, ohne Chats arbeiten"}
                text={strings.kjuup_io_setup_chats_no_text || "Chats werden ausgeblendet. Aus „Nachrichten“ wird „E-Mail“; Projekte, Aufgaben und Tagesberichte bleiben."}
                selected={!chatsEnabled}
                onSelect={() => setChatsEnabled(false)}
              />
            </>
          ) : (
            <>
              <OptionCard
                icon="inbox"
                title={strings.kjuup_io_setup_email_client || "E-Mail-Client in Kjuup"}
                text={strings.kjuup_io_setup_email_client_text || "E-Mails direkt in Kjuup lesen und beantworten."}
                selected={emailMode === "client"}
                onSelect={() => setEmailMode("client")}
              />
              <OptionCard
                icon="summarize"
                title={strings.kjuup_io_setup_email_reports || "Bisheriger E-Mail-Client"}
                text={strings.kjuup_io_setup_email_reports_text || "E-Mails weiter im aktuellen Programm lesen und beantworten, zum Beispiel Outlook oder einem anderen."}
                selected={emailMode === "reports"}
                onSelect={() => setEmailMode("reports")}
              />
            </>
          )}
        </div>

        <div className="io-setup-foot">
          <button type="button" className="kj-btn kj-btn--secondary" onClick={step === 1 ? skip : () => setStep(1)} disabled={saving}>
            {step === 1 ? strings.kjuup_io_setup_skip || "Später" : strings.back || "Zurück"}
          </button>
          <button type="button" className="kj-btn kj-btn--primary" onClick={step === 1 ? () => setStep(2) : finish} disabled={saving}>
            {step === 1
              ? strings.next || "Weiter"
              : saving
                ? strings.saving || "Speichern…"
                : strings.kjuup_io_setup_finish || "Einrichtung abschließen"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
